/* eslint-disable @typescript-eslint/no-explicit-any */

import i18next from "i18next";
import { useCallback } from "react";
import { SweetAlertService } from "../services/alert/sweetAlertService";

const tr = (key: string, defaultValue: string) => i18next.t(key, defaultValue);

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

/** Chuỗi nhiều dòng (message API) → html SweetAlert giữ xuống dòng */
export const formatErrorAlertMessage = (message: string): string => {
    const trimmed = message.trim();
    if (!trimmed) return trimmed;
    if (!trimmed.includes("\n") && !trimmed.includes("\r")) {
        return trimmed;
    }
    return `<div style="text-align:left;white-space:pre-wrap;font-size:14px;line-height:1.5;">${escapeHtml(trimmed)}</div>`;
};

type ApiErrorShape = {
    success?: boolean;
    message?: string;
    errors?: unknown[];
    errorCode?: string;
};

const CHECKSUM_ERROR_MESSAGES = (): Record<string, string> => ({
    CHECKSUM_MISSING: tr(
        "common.error.checksum.missing",
        "Không thể gửi yêu cầu an toàn (thiếu mã xác thực).\n\nVui lòng tải lại trang (Ctrl+F5). Nếu vẫn gặp lỗi, liên hệ quản trị viên."
    ),
    CHECKSUM_EMPTY: tr(
        "common.error.checksum.empty",
        "Mã xác thực yêu cầu bị rỗng.\n\nVui lòng tải lại trang (Ctrl+F5) hoặc đăng xuất và đăng nhập lại."
    ),
    CHECKSUM_INVALID: tr(
        "common.error.checksum.invalid",
        "Xác thực yêu cầu thất bại — dữ liệu có thể đã bị thay đổi hoặc phiên làm việc không đồng bộ.\n\nThử các bước sau:\n• Tải lại trang (Ctrl+F5)\n• Xóa cache trình duyệt\n• Đăng xuất và đăng nhập lại\n\nNếu vẫn lỗi, liên hệ quản trị viên."
    ),
    CHECKSUM_ERROR: tr(
        "common.error.checksum.error",
        "Hệ thống không thể xác thực yêu cầu.\n\nVui lòng thử lại sau vài phút. Nếu lỗi tiếp diễn, liên hệ quản trị viên."
    ),
});

const resolveChecksumClientMessage = (data: ApiErrorShape): string | null => {
    const code = data.errorCode?.trim().toUpperCase();
    if (!code?.startsWith("CHECKSUM_")) {
        return null;
    }

    const messages = CHECKSUM_ERROR_MESSAGES();
    return messages[code] ?? messages.CHECKSUM_ERROR;
};

/** Gom message + errors từ ApiResponse BE (Checksum, controller, ...) */
const extractApiErrorMessage = (data: ApiErrorShape | null | undefined, fallback = ""): string => {
    if (!data || typeof data !== "object") {
        return fallback;
    }

    const checksumMessage = resolveChecksumClientMessage(data);
    if (checksumMessage) {
        return checksumMessage;
    }

    const parts: string[] = [];

    if (typeof data.message === "string" && data.message.trim()) {
        parts.push(data.message.trim());
    }

    if (Array.isArray(data.errors)) {
        data.errors.forEach((item) => {
            const text = String(item ?? "").trim();
            if (text) parts.push(text);
        });
    }

    const unique = [...new Set(parts)];
    if (unique.length > 0) {
        return unique.join("\n");
    }

    return fallback;
};

const HTTP_STATUS_MESSAGES = (): Record<number, string> => ({
    400: tr("common.error.http.400", "Dữ liệu không hợp lệ"),
    401: tr("common.error.http.401", "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại"),
    403: tr("common.error.http.403", "Bạn không có quyền thực hiện thao tác này"),
    404: tr("common.error.http.404", "Không tìm thấy tài nguyên yêu cầu"),
    409: tr("common.error.http.409", "Dữ liệu đã tồn tại hoặc xung đột"),
    422: tr("common.error.http.422", "Dữ liệu không được chấp nhận"),
    429: tr("common.error.http.429", "Quá nhiều yêu cầu. Vui lòng thử lại sau"),
    500: tr("common.error.http.500", "Lỗi hệ thống. Vui lòng liên hệ quản trị viên"),
    502: tr("common.error.http.502", "Lỗi kết nối máy chủ"),
    503: tr("common.error.http.503", "Hệ thống đang bảo trì"),
    504: tr("common.error.http.504", "Hết thời gian chờ kết nối"),
});

export const getErrorMessage = (error: any, defaultMsg: string): string => {
    // Chuỗi trực tiếp (vd: errorHandle("Lỗi tùy chỉnh"))
    if (typeof error === "string" && error.trim()) {
        return error.trim();
    }

    // Axios / HTTP error
    if (error?.response) {
        const { status, data } = error.response;
        const apiMessage = extractApiErrorMessage(data as ApiErrorShape, "");
        if (apiMessage) {
            return apiMessage;
        }

        const statusMessages = HTTP_STATUS_MESSAGES();
        if (statusMessages[status as number]) {
            return statusMessages[status as number];
        }

        if (typeof data === "string" && data.trim()) {
            return data.trim();
        }
    }

    // ApiResponse resolve từ interceptor hoặc gọi trực tiếp: { success: false, message, errors }
    if (error && typeof error === "object" && !error.response) {
        if (error.success === false || "message" in error || "errors" in error) {
            const apiMessage = extractApiErrorMessage(error as ApiErrorShape, "");
            if (apiMessage) {
                return apiMessage;
            }
        }

        if (typeof error.message === "string" && error.message.trim() && !error.errorFields) {
            return error.message.trim();
        }
    }

    if (error?.code === "NETWORK_ERROR" || error?.code === "ERR_NETWORK") {
        return tr("common.error.network", "Lỗi kết nối mạng. Vui lòng kiểm tra internet");
    }

    if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
        return tr("common.error.timeout", "Hết thời gian chờ. Vui lòng thử lại");
    }

    // Ant Design Form validation
    if (error?.errorFields && Array.isArray(error.errorFields)) {
        const missingFields = error.errorFields
            .map((field: any) => (field.errors && field.errors.length > 0 ? field.errors[0] : ""))
            .filter((msg: string) => !!msg)
            .map((msg: string) => msg.replace(/[!.,]/g, ""))
            .join("\n ");
        if (missingFields) {
            return `\n ${missingFields}`;
        }
        return "";
    }

    if (error instanceof Error && error.message.trim()) {
        return `${defaultMsg}: ${error.message.trim()}`;
    }

    return defaultMsg || tr("common.error.unknown", "Lỗi không xác định");
};

export const useErrorHandle = () => {
    const errorHandle = useCallback(async (
        error: any,
        defaultMsg = tr("common.error.default_load", "Lỗi tải dữ liệu!"),
        options: { showConsole?: boolean } = {}
    ) => {
        const errorMessage = getErrorMessage(error, defaultMsg);

        if (options.showConsole) {
            console.error("[errorHandle]", error, errorMessage);
        }

        if (!errorMessage.trim()) {
            return;
        }

        const alertContent = formatErrorAlertMessage(errorMessage);
        await SweetAlertService.warning("", alertContent);
    }, []);

    return { errorHandle, getErrorMessage };
};
